import {
  alphaBetween, solveVertical, solveLevel, solveAlphaZero, solveParallelTrace,
  ALPHA0_INVERT_ALERT, ALPHA0_PARALLEL_NOTE, FLAT_PARALLEL_NOTE,
  AXIS_PARALLEL_NOTE, VERTICAL_CENTRELINE_ALERT, BETA_ZERO_TOLERANCE,
  FLAT_INCLINED_ALERT, coincidenceAlert,
} from "./alphaZero";
import { correctApparentDip } from "./inclination";
import { sectionGeometry } from "./section";

/**
 * Location-dependent alerts become available once the data point is entered.
 * `battered` selects the horseshoe section (walls widening to WS at the SPL).
 * A strike-parallel trace running along log lines names the data point's line,
 * or both lines with `nameBothLines`.
 */
export function inputTraceAlerts(dim, { battered = false, nameBothLines = false } = {}) {
  const D = Number(dim.direction) % 360;
  const Dir = Number(dim.dir) % 360;
  const amt = Number(dim.amt);
  const I = Number(dim.inclination) || 0;
  const geom = sectionGeometry(dim, { battered });
  const args = {
    D, Dir, amt, I, geom, orientation: dim.orientation || "leftinvert",
    p6: { side: dim.tunnelside, d: dim.distancefromcenter, Hp: dim.verticalheight },
  };
  const alpha = alphaBetween(D, Dir);
  let result, note;
  if (amt === 90 && alpha === 0) {
    result = solveVertical(args);
    note = ALPHA0_PARALLEL_NOTE;
  } else if (amt === 0 && I === 0) {
    result = solveLevel(args);
    note = FLAT_PARALLEL_NOTE;
  } else if (alpha === 0 && I === 0) {
    result = solveAlphaZero(args);
    if (result && result.lines) {
      return [coincidenceAlert(nameBothLines ? result.lines : result.lines.slice(0, 1))];
    }
    note = ALPHA0_PARALLEL_NOTE;
  } else if (amt === 0) {
    return [FLAT_INCLINED_ALERT];
  } else if (I !== 0) {
    const beta = Math.atan(Math.tan(amt * Math.PI / 180) * Math.sin(alpha * Math.PI / 180)) * 180 / Math.PI;
    const apDir = Math.cos((Dir - D) * Math.PI / 180) >= 0 ? D : (D + 180) % 360;
    if (correctApparentDip(beta, apDir, D, I).beta < BETA_ZERO_TOLERANCE) {
      result = solveParallelTrace({ ...args, alpha });
      note = AXIS_PARALLEL_NOTE;
    }
  }
  if (!result) return [];
  return [
    result.alert || note,
    result.invertInterior && result.alert !== VERTICAL_CENTRELINE_ALERT ? ALPHA0_INVERT_ALERT : null,
  ].filter(Boolean);
}
